/**
 * Nexus Annotation Keys & Loader Labels (client-safe)
 *
 * Pure helpers for annotation keys and loader text. No server-only deps.
 * Use from client components (embed, agent chat, etc.). Server code can
 * import from nexus-tools-for-chat which re-exports these.
 */

/** CRM tool names that map to crm__ annotation key; all others map to nexus__. */
const CRM_ANNOTATION_TOOL_NAMES = [
  'nexus_crm',
  'create_lead',
  'update_lead',
  'bulk_update_leads',
  'search_leads',
  'get_lead_details',
  'get_email_engagement',
  'get_lead_insights',
  'suggest_follow_up',
  'draft_crm_email',
  'draft_crm_campaign',
  'create_crm_campaign',
  'send_email',
  'send_sms',
  'schedule_activity',
  'log_activity',
  'crm_count',
  'crm_run_query'
] as const

/** Widget-only tools: form/date/time picker. Do not show as annotations—the widget shows the component via interactive_suggestions. */
export const NEXUS_WIDGET_ONLY_TOOLS = [
  'show_contact_form',
  'show_date_picker',
  'show_time_picker'
] as const

export function isNexusWidgetOnlyTool(toolName: string): boolean {
  return NEXUS_WIDGET_ONLY_TOOLS.includes(toolName as (typeof NEXUS_WIDGET_ONLY_TOOLS)[number])
}

/**
 * Annotation key for message annotations (crm__tool_name or nexus__tool_name).
 * Use everywhere we append or render Nexus/CRM tool results so keys stay consistent.
 */
export function getNexusAnnotationKey(toolName: string): string {
  const isCrm = CRM_ANNOTATION_TOOL_NAMES.includes(
    toolName as (typeof CRM_ANNOTATION_TOOL_NAMES)[number]
  )
  return isCrm ? `crm__${toolName}` : `nexus__${toolName}`
}

/** Loader label for each Nexus/CRM tool (tool name -> "Doing X…"). */
export const NEXUS_TOOL_LOADER_LABELS: Record<string, string> = {
  nexus_crm: 'Working in Contacts…',
  NexusCRMTool__run: 'Working in Contacts…',
  create_lead: 'Creating lead…',
  update_lead: 'Updating lead…',
  bulk_update_leads: 'Updating leads…',
  search_leads: 'Searching leads…',
  get_lead_details: 'Loading lead details…',
  get_email_engagement: 'Analyzing email engagement…',
  get_lead_insights: 'Generating lead insights…',
  suggest_follow_up: 'Suggesting follow-up…',
  draft_crm_email: 'Drafting email…',
  draft_crm_campaign: 'Drafting campaign…',
  create_crm_campaign: 'Creating campaign…',
  send_email: 'Sending email…',
  send_sms: 'Sending SMS…',
  schedule_activity: 'Scheduling activity…',
  log_activity: 'Logging activity…',
  crm_count: 'Counting CRM records…',
  crm_run_query: 'Running CRM query…',
  buildCampaignDraft: 'Building campaign draft…',
  createCampaignFromIntent: 'Creating campaign draft…',
  list_agents: 'Listing Nexus agents…',
  get_agent: 'Loading Nexus agent…',
  create_agent: 'Creating Nexus agent…',
  update_agent: 'Updating Nexus agent…',
  improve_agent: 'Improving Nexus agent…',
  list_workspace_progress: 'Loading Progress…',
  create_workspace_progress_goal: 'Creating Progress goal…',
  update_workspace_progress_goal: 'Updating Progress goal…',
  create_workspace_progress_badges: 'Creating Progress badges…',
  magic_generate_workspace_progress: 'Generating Progress setup…',
  recalculate_workspace_progress: 'Recalculating Progress…',
  nexus_booking: 'Working on booking…',
  list_services: 'Listing services…',
  create_service: 'Creating service…',
  create_resource: 'Creating resource…',
  link_resource_to_service: 'Linking resource to service…',
  assign_space_task_to_agent: 'Assigning task…',
  schedule_space_agent_watcher: 'Scheduling watcher…',
  run_space_agent_action: 'Creating agent task…',
  leave_note: 'Saving note…',
  add_note: 'Saving note…',
  schedule_appointment: 'Scheduling your appointment…',
  book_appointment: 'Booking your appointment…',
  book_medspa_appointment: 'Booking your appointment…',
  book_jane_appointment: 'Booking your appointment…',
  list_jobber_clients: 'Listing Jobber clients…',
  list_jobber_jobs: 'Listing Jobber jobs…',
  get_jobber_client: 'Loading Jobber client…',
  get_jobber_job: 'Loading Jobber job…',
  list_clio_matters: 'Listing Clio matters…',
  list_clio_contacts: 'Listing Clio contacts…',
  get_clio_matter: 'Loading Clio matter…',
  get_clio_contact: 'Loading Clio contact…',
  list_phorest_branches: 'Listing Phorest branches…',
  list_phorest_clients: 'Listing Phorest clients…',
  get_phorest_client: 'Loading Phorest client…',
  list_phorest_services: 'Listing Phorest services…',
  create_phorest_booking: 'Creating Phorest booking…',
  list_followupboss_people: 'Listing Follow Up Boss people…',
  get_followupboss_person: 'Loading Follow Up Boss person…',
  list_dentally_patients: 'Listing Dentally patients…',
  get_dentally_patient: 'Loading Dentally patient…',
  list_housecallpro_customers: 'Listing HousecallPro customers…',
  get_housecallpro_customer: 'Loading HousecallPro customer…',
  list_housecallpro_jobs: 'Listing HousecallPro jobs…',
  get_housecallpro_job: 'Loading HousecallPro job…',
  update_housecallpro_customer: 'Updating HousecallPro customer…',
  update_housecallpro_job: 'Updating HousecallPro job…',
  list_doorloop_properties: 'Listing DoorLoop properties…',
  get_doorloop_property: 'Loading DoorLoop property…',
  list_doorloop_tenants: 'Listing DoorLoop tenants…',
  get_doorloop_tenant: 'Loading DoorLoop tenant…',
  update_doorloop_tenant: 'Updating DoorLoop tenant…',
  list_released_contacts: 'Listing Re-Leased contacts…',
  list_released_properties: 'Listing Re-Leased properties…',
  get_released_property: 'Loading Re-Leased property…',
  list_released_tenancies: 'Listing Re-Leased tenancies…',
  get_released_tenancy: 'Loading Re-Leased tenancy…',
  list_finmo_applications: 'Listing Finmo applications…',
  get_finmo_application: 'Loading Finmo application…',
  update_finmo_application_status: 'Updating Finmo application…',
  list_glofox_members: 'Listing Glofox members…',
  get_glofox_member: 'Loading Glofox member…',
  list_opendental_patients: 'Listing Open Dental patients…',
  get_opendental_patient: 'Loading Open Dental patient…',
  list_practicepanther_contacts: 'Listing Practice Panther contacts…',
  get_practicepanther_contact: 'Loading Practice Panther contact…',
  list_cosmolex_matters: 'Listing CosmoLex matters…',
  get_cosmolex_matter: 'Loading CosmoLex matter…',
  list_leap_matters: 'Listing LEAP matters…',
  get_leap_matter: 'Loading LEAP matter…',
  list_yeti_jobs: 'Listing Yeti jobs…',
  get_yeti_job: 'Loading Yeti job…',
  list_tidy_jobs: 'Listing TIDY jobs…',
  get_tidy_job: 'Loading TIDY job…',
  list_realgreen_customers: 'Listing RealGreen customers…',
  get_realgreen_customer: 'Loading RealGreen customer…',
  list_roofiq_jobs: 'Listing RoofIQ jobs…',
  get_roofiq_job: 'Loading RoofIQ job…',
  list_fieldedge_jobs: 'Listing FieldEdge jobs…',
  get_fieldedge_job: 'Loading FieldEdge job…',
  list_jobnimbus_contacts: 'Listing JobNimbus contacts…',
  get_jobnimbus_contact: 'Loading JobNimbus contact…',
  list_servicefusion_jobs: 'Listing Service Fusion jobs…',
  get_servicefusion_job: 'Loading Service Fusion job…',
  list_workiz_jobs: 'Listing Workiz jobs…',
  get_workiz_job: 'Loading Workiz job…',
  list_ezyvet_clients: 'Listing ezyVet clients…',
  get_ezyvet_client: 'Loading ezyVet client…',
  list_petexec_clients: 'Listing PetExec clients…',
  get_petexec_client: 'Loading PetExec client…',
  list_provetcloud_clients: 'Listing Provet Cloud clients…',
  get_provetcloud_client: 'Loading Provet Cloud client…',
  list_gingr_clients: 'Listing Gingr clients…',
  get_gingr_client: 'Loading Gingr client…',
  list_shopify_customers: 'Listing Shopify customers…',
  get_shopify_customer: 'Loading Shopify customer…',
  list_revel_customers: 'Listing Revel customers…',
  get_revel_customer: 'Loading Revel customer…',
  list_iqmetrix_customers: 'Listing iQmetrix customers…',
  get_iqmetrix_customer: 'Loading iQmetrix customer…',
  list_vendo_customers: 'Listing Vendo customers…',
  get_vendo_customer: 'Loading Vendo customer…',
  list_moverbase_moves: 'Listing Moverbase moves…',
  get_moverbase_move: 'Loading Moverbase move…',
  list_smartmoving_moves: 'Listing SmartMoving moves…',
  get_smartmoving_move: 'Loading SmartMoving move…',
  list_stora_moves: 'Listing Stora moves…',
  get_stora_move: 'Loading Stora move…',
  list_kinnovis_moves: 'Listing Kinnovis moves…',
  get_kinnovis_move: 'Loading Kinnovis move…',
  list_chariot_customers: 'Listing Chariot customers…',
  get_chariot_customer: 'Loading Chariot customer…',
  list_childcarecrm_families: 'Listing ChildCareCRM families…',
  get_childcarecrm_family: 'Loading ChildCareCRM family…',
  list_passare_cases: 'Listing Passare cases…',
  get_passare_case: 'Loading Passare case…',
  list_wink_patients: 'Listing WINK patients…',
  get_wink_patient: 'Loading WINK patient…',
  list_pronotary_sessions: 'Listing ProNotary sessions…',
  get_pronotary_session: 'Loading ProNotary session…',
  list_notarylive_sessions: 'Listing NotaryLive sessions…',
  get_notarylive_session: 'Loading NotaryLive session…',
  list_procore_projects: 'Listing Procore projects…',
  get_procore_project: 'Loading Procore project…',
  list_cleancloud_customers: 'Listing CleanCloud customers…',
  get_cleancloud_customer: 'Loading CleanCloud customer…',
  list_spot_locations: 'Listing SPOT locations…',
  get_spot_location: 'Loading SPOT location…',
  list_pooli_customers: 'Listing Pooli customers…',
  get_pooli_customer: 'Loading Pooli customer…',
  list_cdkglobal_catalogs: 'Listing CDK Global catalogs…',
  get_cdkglobal_catalog: 'Loading CDK Global catalog…',
  list_activix_contacts: 'Listing Activix contacts…',
  get_activix_contact: 'Loading Activix contact…',
  list_dealerpeak_customers: 'Listing DealerPeak customers…',
  get_dealerpeak_customer: 'Loading DealerPeak customer…',
  list_cloudbeds_properties: 'Listing Cloudbeds properties…',
  get_cloudbeds_property: 'Loading Cloudbeds property…',
  list_siteminder_properties: 'Listing SiteMinder properties…',
  get_siteminder_property: 'Loading SiteMinder property…',
  list_postgrid_contacts: 'Listing PostGrid contacts…',
  get_postgrid_contact: 'Loading PostGrid contact…',
  list_shootproof_studios: 'Listing ShootProof studios…',
  get_shootproof_studio: 'Loading ShootProof studio…',
  list_photostack_clients: 'Listing PhotoStack clients…',
  get_photostack_client: 'Loading PhotoStack client…',
  list_boxtracker_shipments: 'Listing Box Tracker shipments…',
  get_boxtracker_shipment: 'Loading Box Tracker shipment…',
  list_teranet_documents: 'Listing Teranet documents…',
  get_teranet_document: 'Loading Teranet document…',
  list_ownright_properties: 'Listing Ownright properties…',
  get_ownright_property: 'Loading Ownright property…',
  list_inspectordata_reports: 'Listing InspectorData reports…',
  get_inspectordata_report: 'Loading InspectorData report…',
  list_propertyinspect_inspections: 'Listing PropertyInspect inspections…',
  get_propertyinspect_inspection: 'Loading PropertyInspect inspection…',
  list_teachworks_students: 'Listing Teachworks students…',
  get_teachworks_student: 'Loading Teachworks student…',
  list_classmarker_tests: 'Listing ClassMarker tests…',
  get_classmarker_test: 'Loading ClassMarker test…',
  list_cleanlist_contacts: 'Listing Cleanlist contacts…',
  get_cleanlist_contact: 'Loading Cleanlist contact…',
  list_airthings_devices: 'Listing Airthings devices…',
  get_airthings_device: 'Loading Airthings device…',
  list_enginehire_candidates: 'Listing EngineHire candidates…',
  get_enginehire_candidate: 'Loading EngineHire candidate…',
  list_ashby_jobs: 'Listing Ashby jobs…',
  get_ashby_job: 'Loading Ashby job…',
  list_staffstat_shifts: 'Listing StaffStat shifts…',
  get_staffstat_shift: 'Loading StaffStat shift…',
  list_rippling_employees: 'Listing Rippling employees…',
  get_rippling_employee: 'Loading Rippling employee…',
  list_collagehr_employees: 'Listing Collage HR employees…',
  get_collagehr_employee: 'Loading Collage HR employee…',
  list_quotewerks_contacts: 'Listing QuoteWerks contacts…',
  get_quotewerks_contact: 'Loading QuoteWerks contact…',
  list_freshbooks_clients: 'Listing FreshBooks clients…',
  get_freshbooks_client: 'Loading FreshBooks client…',
  list_zohobooks_contacts: 'Listing Zoho Books contacts…',
  get_zohobooks_contact: 'Loading Zoho Books contact…',
  book_mindbody_appointment: 'Booking your appointment…',
  book_vagaro_appointment: 'Booking your appointment…',
  list_available_slots: 'Checking availability…',
  search_delivery_catalog: 'Searching the catalog…',
  check_item_stock: 'Checking stock…',
  quote_delivery: 'Quoting delivery…',
  create_delivery_order: 'Placing the delivery order…',
  get_delivery_status: 'Checking delivery status…',
  report_inventory_gap: 'Finding a restock option…',
  report_age_verification_failure: 'Handling the failed delivery…',
  create_booking: 'Creating your booking…',
  cancel_booking: 'Cancelling booking…',
  reschedule_booking: 'Rescheduling booking…',
  get_booking_details: 'Loading booking details…',
  list_customer_bookings: 'Loading your bookings…',
  list_services: 'Loading services…',
  send_booking_confirmation: 'Sending confirmation…',
  add_booking_note: 'Saving note…',
  get_available_appointment_slots: 'Checking availability…',
  lookup_information: 'Looking that up…',
  search_info: 'Searching…',
  get_weather: 'Checking weather…',
  create_task: 'Adding task…',
  add_task: 'Adding task…',
  transfer_to_human: 'Connecting you to a team member…',
  transfer_call: 'Transferring call…',
  schedule_best_available_appointment: 'Finding a time…',
  get_upcoming_appointments: 'Loading your appointments…',
  cancel_appointment: 'Cancelling appointment…',
  reschedule_appointment: 'Rescheduling…',
  create_calendar_event: 'Adding to calendar…',
  send_message: 'Sending message…',
  create_ticket: 'Submitting ticket…',
  search_tickets: 'Searching tickets…',
  make_outbound_call: 'Starting call…',
  initiate_call: 'Starting call…',
  call_number: 'Calling…',
  // Voice & Communication tools
  list_calls: 'Loading call history…',
  get_call_details: 'Loading call details…',
  list_voicemails: 'Loading voicemails…',
  get_call_analytics: 'Analyzing call data…',
  update_communication_preferences: 'Updating preferences…',
  list_phone_numbers: 'Loading phone numbers…',
  send_sms_voice: 'Sending SMS…',
  // Integration tools
  list_integrations: 'Loading integrations…',
  get_integration_health: 'Checking integration health…',
  sync_integration: 'Syncing integration…',
  list_webhooks: 'Loading webhooks…',
  create_webhook: 'Creating webhook…',
  list_integration_events: 'Loading integration events…',
  create_api_key: 'Generating API key…',
  revoke_api_key: 'Revoking API key…',
  get_sync_status: 'Checking sync status…',
  reconnect_integration: 'Reconnecting integration…',
  // Agent performance tools
  get_agent_performance: 'Analyzing agent performance…',
  list_active_conversations: 'Loading active conversations…',
  compare_agents: 'Comparing agents…',
  rollback_agent: 'Rolling back agent…',
  create_ab_test: 'Creating A/B test…',
  // CRM assignment tools
  assign_entity: 'Assigning contact…',
  bulk_assign: 'Assigning contacts…',
  link_entities_to_task: 'Linking to task…',
  // GitHub tools
  list_repos: 'Listing GitHub repos…',
  get_repo: 'Loading GitHub repo…',
  list_branches: 'Listing GitHub branches…',
  list_issues: 'Listing GitHub issues…',
  get_issue: 'Loading GitHub issue…',
  create_issue: 'Creating GitHub issue…',
  update_issue: 'Updating GitHub issue…',
  comment_issue: 'Commenting on GitHub issue…',
  list_pull_requests: 'Listing pull requests…',
  get_pull_request: 'Loading pull request…',
  create_pull_request: 'Creating pull request…',
  merge_pull_request: 'Merging pull request…',
  // Losi Code
  check_coding_readiness: 'Checking coding readiness…',
  request_connector: 'Requesting coding connector…',
  ensure_coding_task: 'Preparing coding task…',
  start_coding_run: 'Starting coding run…',
  run_verification: 'Running verification…',
  get_coding_run_status: 'Checking coding run…',
  get_file: 'Loading GitHub file…',
  list_commits: 'Listing GitHub commits…',
  search_code: 'Searching GitHub code…'
}
